import { NavLink, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { motion } from "motion/react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarHeader,
  SidebarRail,
  SidebarSeparator,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { useSiteSelector } from "@/hooks/useSiteSelector";
import { SidebarHeaderMenu } from "@/components/SidebarHeaderMenu";
import { cn } from "@/lib/utils";
import { useUnreadNotifications } from "@/hooks/useUnreadNotifications";
import { useIsMobile } from "@/hooks/useMobile";
import { useGlobalAgent } from "@/contexts/GlobalAgentContext";
import { Plus, Truck } from "lucide-react";
import { SITE_MANAGER_SIDEBAR_NAV } from "@/config/navigation";

export function SiteManagerSidebar() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const { selectedSite: selectedChantier } = useSiteSelector();
  const { unreadCount } = useUnreadNotifications(selectedChantier?.id);
  const isMobile = useIsMobile();
  const { state: agentState } = useGlobalAgent();
  const hasAnimated = useRef(false);

  const handlePrefetch = (url: string) => {
    if (!selectedChantier?.id) return;
    const siteId = selectedChantier.id;

    switch (url) {
      case '/site':
        queryClient.prefetchQuery({ queryKey: ['site-detail', siteId], staleTime: 30 * 1000 });
        queryClient.prefetchQuery({ queryKey: ['site-budget-analysis', siteId], staleTime: 30 * 1000 });
        break;
      case '/site/progress':
        queryClient.prefetchQuery({ queryKey: ['site-budget-analysis', siteId], staleTime: 30 * 1000 });
        break;
      case '/site/planning':
        queryClient.prefetchQuery({ queryKey: ['gantt-data', siteId], staleTime: 30 * 1000 });
        break;
      case '/site/history':
        queryClient.prefetchQuery({ queryKey: ['siteBetonnages', siteId], staleTime: 30 * 1000 });
        break;
      case '/site/calendar':
        queryClient.prefetchQuery({ queryKey: ['site-calendar', siteId], staleTime: 30 * 1000 });
        break;
    }
  };

  const getInitialAnimation = () => {
    if (hasAnimated.current) return false;
    return { opacity: 0, x: -12 };
  };

  return (
    <Sidebar collapsible={isMobile ? "offcanvas" : "none"} variant="sidebar" className="border-none h-screen">
      <SidebarHeader className="bg-sidebar py-sm px-sm">
        <SidebarHeaderMenu variant="site_manager" />
      </SidebarHeader>

      <SidebarSeparator />

      <SidebarContent className="bg-sidebar overflow-hidden flex-1">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {SITE_MANAGER_SIDEBAR_NAV.map((item, index) => {
                const isActive = item.exact
                  ? location.pathname === item.url
                  : location.pathname === item.url || location.pathname.startsWith(item.url + "/");

                const showBadge = item.title === "Notifications" && unreadCount > 0;

                return (
                  <motion.div
                    key={item.title}
                    initial={getInitialAnimation()}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      duration: 0.2,
                      delay: hasAnimated.current ? 0 : index * 0.03,
                      ease: [0.4, 0, 0.2, 1],
                    }}
                    onAnimationComplete={() => {
                      if (index === SITE_MANAGER_SIDEBAR_NAV.length - 1) {
                        hasAnimated.current = true;
                      }
                    }}
                  >
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={isActive}
                        className={cn(
                          "text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors",
                          isActive && "bg-accent text-foreground",
                        )}
                      >
                        <NavLink
                          to={item.url}
                          className="flex items-center justify-start gap-sm"
                          onMouseEnter={() => handlePrefetch(item.url)}
                        >
                          <item.icon className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                          <span className="flex-1 text-body-md">{item.title}</span>
                          {showBadge && (
                            <Badge
                              variant="destructive"
                              className="h-5 min-w-5 flex items-center justify-center text-body-sm px-1.5"
                            >
                              {unreadCount > 99 ? '99+' : unreadCount}
                            </Badge>
                          )}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </motion.div>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

      </SidebarContent>

      <SidebarFooter className="bg-sidebar px-md pt-md pb-xl gap-1">
        <NavLink
          to="/site?reservation=true"
          className="flex items-center justify-center gap-sm w-full h-12 rounded-lg bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors text-body-md font-medium"
        >
          <Truck className="w-4 h-4 shrink-0" strokeWidth={2} />
          Pré-commander
        </NavLink>
        <NavLink
          to="/site?nouveau=true"
          className="flex items-center justify-center gap-sm w-full h-12 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-body-md font-medium"
        >
          <Plus className="w-4 h-4 shrink-0" strokeWidth={2} />
          Nouveau bétonnage
        </NavLink>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
